import {
  Alert,
  Box, Card, CardContent, CircularProgress, Dialog, DialogContent, FormControl,
  FormControlLabel, Grid, IconButton, List,
  ListItem, ListItemText,
  Radio, RadioGroup, Snackbar, TextField, Typography, useTheme,
} from '@mui/material';
import { red } from '@mui/material/colors';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  ChangeEvent, useEffect, useState,
} from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { pascalCase } from 'change-case';
import type { UpdateCompanyPartnerRequest } from '../../../api/dashboardAPI';
import OnboardingLayout from './common/OnboardingLayout';
import { ReactComponent as NewTabIcon } from '../../../assets/images/new_tab_icon.svg';
import {
  emailRegex, getCanonicalPartnerName, HOME_DEPOT_NAME, PartnerName,
} from '../../../common/constants';
import {
  enrollCompanyToPartner,
  getCompanyPartnerRelations,
  updateCompanyPartner,
} from '../../../store/thunks/dashboardThunk';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

import {
  selectHdDesktopOnboardShowIsDoneState, selectMemberInformation,
  setHdDesktopOnboardShowIsDoneState, updateNavigationFromOnboarding,
} from '../../../store/slices/onboardingSlice';
import VideoPlayer from '../../../common/VideoPlayer';
import { PostponeHdSetup } from './common/PostponeHdStep';
import { TestIds } from '../../../assets/testIds';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { OnboardingStepper } from '../../../common/OnboardingStepper';
import { useNavigationUtils } from '../../../common/utils/useNavigationUtils';
import { useOnboardingRedirectPath } from '../../../common/useOnboardingRedirectPath';
import { CopyPill } from '../../../common/CopyPill';
import OptimusButton from '../../../common/library/Button/Button';
import { OnboardHDShopQuestionV2 } from './OnboardHDShopQuestionV2';
import { getMemberInformation } from '../../../store/thunks/onboardingThunk';
import { Logger } from '../../../config/logging/logger';
import { isUserInitiatedHomeDepotEnrollment } from '../../../common/homeDepotEnrollmentIntent';
import { PartnerEnrollmentStatus, PartnerEnrollType } from '../../../common/enums';

interface IFormInputs {
  accountStatus: string;
  email: string
}

export const SHOW_EXISTING_ACCT_QUESTION_ACTION = 'Existing account question';
export const SHOW_SIGN_IN_OR_SIGN_UP_ACTION = 'Sign in or sign up';
export const SHOW_IS_DONE_ACTION = 'Did you complete all the steps?';
export const SHOW_EMAIL_ACTION = 'Enter email';

export function OnboardHDHomeWeb() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    register, handleSubmit, control, formState, setValue, clearErrors,
  } = useForm<IFormInputs>({ mode: 'onChange' });
  const [showSignInOrSignUp, setShowSignInOrSignUp] = useState(false);
  const [showExistingAcctQuestion, setShowExistingAcctQuestion] = useState(true);
  const [showIsDone, setShowIsDone] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [hdEmail, setHdEmail] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const { trackPartnerEnrollment, trackRequest } = useCustomEventTracking();
  const { company } = useAppSelector(selectMemberInformation);
  const hdDesktopOnboardShowIsDoneState = useAppSelector(selectHdDesktopOnboardShowIsDoneState);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { openExternalLink } = useNavigationUtils();
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const signInVideoUrl = 'https://d2o7bp9oz3gksv.cloudfront.net/sign_in_flow_044a3d8ae3.mp4';
  const signUpVideoUrl = 'https://d2o7bp9oz3gksv.cloudfront.net/sign_up_flow_beef0edde8.mp4';
  const location = useLocation();
  const userInitiatedHdEnrollment = isUserInitiatedHomeDepotEnrollment({
    state: location.state,
    search: location.search,
  });
  const [showHDShopQuestion, setShowHDShopQuestion] =
    useState(!userInitiatedHdEnrollment);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const logger = Logger.create('OnboardHDHomeWeb');

  // Extract initiating partner info from navigation state
  const initiatingPartnerName = location.state?.initiatingPartnerName as PartnerName | undefined;
  const initiatingPartnerCode = location.state?.initiatingPartnerCode as string | undefined;
  const initiatingPartnerId = location.state?.initiatingPartnerId as string | undefined;

  // get redirect path
  const redirectPath = useOnboardingRedirectPath({
    redirectToPartnersForOnboardingFlowTestOff: true,
  });
  useEffect(() => {
    setShowHDShopQuestion(!userInitiatedHdEnrollment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch the selected radio button value
  // const selectedOption = watch('accountStatus');

  const setSecondaryCardState = (triggerAction: string) => {
    switch (triggerAction) {
      case SHOW_EXISTING_ACCT_QUESTION_ACTION:
        setShowExistingAcctQuestion(true);
        setShowSignInOrSignUp(false);
        setShowIsDone(false);
        setShowEmail(false);
        setOpenVideoDialog(false);
        break;
      case SHOW_SIGN_IN_OR_SIGN_UP_ACTION:
        setShowSignInOrSignUp(true);
        setShowIsDone(false);
        setShowEmail(false);
        setShowExistingAcctQuestion(false);
        break;
      case SHOW_IS_DONE_ACTION:
        setShowIsDone(true);
        setShowEmail(false);
        setShowSignInOrSignUp(false);
        setShowExistingAcctQuestion(false);
        setOpenVideoDialog(false);
        break;
      case SHOW_EMAIL_ACTION:
        setShowEmail(true);
        setShowIsDone(false);
        setShowSignInOrSignUp(false);
        setShowExistingAcctQuestion(false);
        setOpenVideoDialog(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (hdDesktopOnboardShowIsDoneState) {
      setSecondaryCardState(SHOW_IS_DONE_ACTION);
    }
  }, [hdDesktopOnboardShowIsDoneState]);

  const onSubmit = async (data: IFormInputs) => {
    if (data.email) {
      setLoading(true);
      const request: UpdateCompanyPartnerRequest = {
        partnerMemberEmail: data.email,
        enrollmentStatus: PartnerEnrollmentStatus.InProgress,
      };

      try {
        // Complete Home Depot enrollment
        await dispatch(updateCompanyPartner({
          companyId: company?.id || '',
          partnerId: process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '',
          data: request,
        })).then(unwrapResult);

        setOpenErrorSnackbar(false);

        trackPartnerEnrollment('Submit', 'HomeDepotAccountEmail', {
          homeDepotEmail: data.email,
          isOptimizelyEvent: true,
        });

        trackPartnerEnrollment('Complete', 'HomeDepotSignup', {
          homeDepotEmail: data.email,
          isOptimizelyEvent: true,
        });

        // fire event to indicate company submitted partner enrollment
        trackPartnerEnrollment('Submit', 'Form', {
          partnerName: HOME_DEPOT_NAME,
          enrollmentType: 'Unknown',
          isOptimizelyEvent: true,
        });

        // Auto-enroll in initiating partner (e.g., Behr)
        // if Home Depot enrollment was started from partner flow
        if (initiatingPartnerName && initiatingPartnerId) {
          try {
            const companyId = company?.id;
            const homeDepotPartnerId = process.env.REACT_APP_HOME_DEPOT_PARTNER_ID;
            if (!companyId || !homeDepotPartnerId) {
              throw new Error('Company or Home Depot partner not found');
            }
            await dispatch(enrollCompanyToPartner({
              companyId,
              partnerId: initiatingPartnerId,
              data: {
                type: PartnerEnrollType.Manufacturer,
                enrollData: { distributorId: homeDepotPartnerId },
              },
            })).then(unwrapResult);

            // Refresh member information and company partner relations
            await dispatch(getMemberInformation({ trackRequest }));
            await dispatch(getCompanyPartnerRelations(companyId));

            // fire event to indicate member has signed up with initiating partner.
            // SN-838: derive the event name from the rename-stable canonical name
            // (keyed off the partner code) so a Retool rename does not fork the funnel.
            trackPartnerEnrollment('Submit', `${pascalCase(getCanonicalPartnerName(initiatingPartnerCode, initiatingPartnerName))}SignupForm`, {
              formData: null,
              isOptimizelyEvent: true,
            });

            // fire event to indicate company submitted partner enrollment
            trackPartnerEnrollment('Submit', 'Form', {
              partnerName: initiatingPartnerName,
              enrollmentType: 'New',
              isOptimizelyEvent: true,
            });
          } catch (autoEnrollError) {
            // Log but don't block navigation if auto-enrollment fails
            logger.error('Auto-enrollment in initiating partner failed:', autoEnrollError);
          }
        }

        // navigate to content home page or return path
        dispatch(updateNavigationFromOnboarding(true));

        // We deprecated the rewards page in favor of the partners page
        navigate(redirectPath as string, {
          state: { fromOnboarding: true },
        });
      } catch (error: unknown) {
        setOpenErrorSnackbar(true);
        setLoading(false);
      }
    }
  };

  const handleErrorClose = () => {
    setOpenErrorSnackbar(false);
  };

  const handleReturnToShowExistAccQns = () => {
    setValue('accountStatus', '');
    setSelectedOption('');
    setSecondaryCardState(SHOW_EXISTING_ACCT_QUESTION_ACTION);
  };

  const handleReturnToShowLinkAccount = () => {
    setSecondaryCardState(SHOW_SIGN_IN_OR_SIGN_UP_ACTION);
    // clear state in store
    dispatch(setHdDesktopOnboardShowIsDoneState(false));
  };

  // Handle the radio button change
  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (hdEmail) {
      setHdEmail('');
      setValue('email', ''); // reset the email field
      clearErrors('email'); // Clear any existing validation errors for the email field
    }
    setSelectedOption(e.target.value);
    setSecondaryCardState(SHOW_SIGN_IN_OR_SIGN_UP_ACTION);
    trackPartnerEnrollment('Submit', 'HomeDepotProxtraAccountStatus', {
      existingProXtraAccount: e.target.value,
    });

    // Answering the Pro Xtra question is the first real sign of intent on this page, so
    // mark the relation as `action_required` instead of leaving it `not_started` if the
    // member drops off before submitting their email. Silent and fire-and-forget: the
    // answer only advances the card locally and must not be gated on this call.
    const actionRequiredRequest: UpdateCompanyPartnerRequest = {
      enrollmentStatus: PartnerEnrollmentStatus.ActionRequired,
    };
    dispatch(updateCompanyPartner({
      companyId: company?.id || '',
      partnerId: process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '',
      data: actionRequiredRequest,
    }))
      .then(unwrapResult)
      .catch((error: unknown) => {
        logger.error(Logger.format(error));
      });
  };

  const onGoToHomeDepot = () => {
    trackPartnerEnrollment('Click', 'HomeDepotSignIn');
    trackPartnerEnrollment('Click', 'HomeDepotEnterCode');
    openExternalLink('https://www.homedepot.com/myaccount/proXtraPricing');
  };

  const onLinkedMyAccount = () => {
    setSecondaryCardState(SHOW_IS_DONE_ACTION);
    trackPartnerEnrollment('Click', 'HomeDepotLinkedMyAccount');
    // persist in store (needed for webview navigation)
    dispatch(setHdDesktopOnboardShowIsDoneState(true));
  };

  const onYesDoneClick = () => {
    setSecondaryCardState(SHOW_EMAIL_ACTION);
    trackPartnerEnrollment('Click', 'HomeDepotSetupDone');
    // clear state in store
    dispatch(setHdDesktopOnboardShowIsDoneState(false));
  };

  const onNotDoneClick = () => {
    setSecondaryCardState(SHOW_SIGN_IN_OR_SIGN_UP_ACTION);
    trackPartnerEnrollment('Click', 'HomeDepotSetupReset');
    // clear state in store
    dispatch(setHdDesktopOnboardShowIsDoneState(false));
  };

  const handleOnPlay = () => {
    trackPartnerEnrollment('Play', 'HomeDepotOnboardingVideo', {
      videoUrl: selectedOption === 'yes' ? signInVideoUrl : signUpVideoUrl,
    });
  };

  const handleOnEnd = () => {
    trackPartnerEnrollment('Complete', 'HomeDepotOnboardingVideo', {
      videoUrl: selectedOption === 'yes' ? signInVideoUrl : signUpVideoUrl,
    });
  };

  const onVideoDialogClose = () => {
    setOpenVideoDialog(false);
  };

  const VideoDialog = () => (
    <Dialog
      open={openVideoDialog}
      keepMounted
      onClose={onVideoDialogClose}
      aria-describedby={'home-depot-video-dialog'}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          borderRadius: theme.spacing(2.25),
          [theme.breakpoints.down('lg')]: {
            p: theme.spacing(0),
          },
        },
      }}
    >
      <DialogContent
        sx={{ paddingRight: theme.spacing(3) }}
      >
        <IconButton
          aria-label="close"
          onClick={onVideoDialogClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.brand.primary,
          }}
        >
          <CloseIcon />
        </IconButton>
        <VideoPlayer
          videoUrl={selectedOption === 'yes' ? signInVideoUrl : signUpVideoUrl}
          optionalVideoContainerStyle={{
            marginTop: theme.spacing(4), // Video Margin Top to fit screen
            marginLeft: 'auto',
            marginRight: 'auto',
            background: theme.palette.common.black,
            maxWidth: theme.spacing(72),
            borderRadius: theme.spacing(2.41),
            padding: theme.spacing(3.125),
            height: '60vh',
          }}
          handleOnPlay={handleOnPlay}
          handleOnEnd={handleOnEnd}
        />
      </DialogContent>
    </Dialog >
  );

  const SignInOrSignUpCard = () => (
    <Box
      sx={{
        textAlign: 'left',
        mt: theme.spacing(3),
        p: theme.spacing(3),
        borderRadius: theme.spacing(1.5),
        background: theme.palette.common.white,
        boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.02), 0 0 8px 0 rgba(0, 0, 0, 0.04)',
      }}
    >
      <Typography
        variant = 'h15'
        component={'div'}
        sx={{
          mb: theme.spacing(3),
        }}>
        2. {t('hdSetUpWeb.heading5')}
      </Typography>
      <List sx={{ padding: 0, margin: 0, pl: theme.spacing(1) }}>
        <ListItem sx={{ p: 0, mb: theme.spacing(2.5) }}>
          <ListItemText
            primary={
              <Typography component="span">
                <Trans
                  i18nKey={selectedOption === 'yes' ?
                    'hdSetUpWeb.signInOrSignUp.signInTextB' :
                    'hdSetUpWeb.signInOrSignUp.signUpTextB'
                  }
                  t={t}
                  components={{
                    linkTag: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <Typography
                        component={'span'}
                        onClick={onGoToHomeDepot}
                        sx={{
                          color: theme.palette.brand.primary,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      />
                    ),
                  }}
                />
              </Typography>
            }
            sx={{
              mt: 0,
              mb: 0,
            }}
          />
        </ListItem>
        <ListItem sx={{ p: 0, mb: theme.spacing(2.5) }}>
          <ListItemText
            primary={
              <Typography component="span">
                {t('hdSetUpWeb.signInOrSignUp.enterCodeTextB')}
              </Typography>
            }
            sx={{
              mt: 0,
              mb: 0,
            }}
          />
        </ListItem>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: theme.spacing(3),
          mb: theme.spacing(3),
        }}>
          <CopyPill label="JOIST" />
        </Box>
        <ListItem sx={{ p: 0, mb: theme.spacing(3) }}>
          <ListItemText
            primary={
              <Typography component="span">
                {t('hdSetUpWeb.signInOrSignUp.completeYourSetupText')}
              </Typography>
            }
            sx={{
              mt: 0,
              mb: 0,
            }}
          />
        </ListItem>
      </List>
      <OptimusButton
        size={'m'}
        sx={{
          mb: theme.spacing(2),
        }}
        trailingIcon={<NewTabIcon />}
        buttonText={t('hdSetUpWeb.signInOrSignUp.launchHdBtnTextB') as string}
        data-testid={TestIds.onboarding.homedepotLaunchBtn}
        onClick={onGoToHomeDepot}
      />
      <OptimusButton
        size={'m'}
        customVariant={'secondaryGray'}
        buttonText={t('hdSetUpWeb.signInOrSignUp.whenDoneTextB') as string}
        data-testid={TestIds.onboarding.homedepotLinkedMyAccountBtn}
        onClick={onLinkedMyAccount}
      />
      <Grid container spacing={1}
        sx={{
          mt: theme.spacing(3),
          p: `${theme.spacing(1)} ${theme.spacing(2)}`,
          borderRadius: theme.spacing(1),
          background: theme.palette.grey[100],
          alignItems: 'center',
        }}>
        <Grid item xs={12} md={5.5}>
          <Typography component="div" variant='body8'>
            {t('hdSetUpWeb.signInOrSignUp.needGuidance')}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6.5}>
          <OptimusButton
            size={'s'}
            customVariant={'secondaryGray'}
            buttonText={t('hdSetUpWeb.signInOrSignUp.watchVideo') as string}
            leadingIcon={PlayCircleOutlinedIcon}
            onClick={() => setOpenVideoDialog(true)}
            sx={{
              whiteSpace: 'nowrap',
            }}
          />
        </Grid>
      </Grid>
      <VideoDialog />
    </Box>
  );

  const IsDoneCard = () => (
    <Box sx={{
      textAlign: 'left',
      mt: theme.spacing(3),
      p: theme.spacing(3),
      borderRadius: theme.spacing(1.5),
      background: theme.palette.common.white,
      boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.02), 0 0 8px 0 rgba(0, 0, 0, 0.04)',

    }}>
      <Typography
        variant = 'h15'
        component={'div'}
        sx={{
          mb: theme.spacing(3),
        }}>
        2. {t('hdSetUpWeb.heading5')}
      </Typography>
      <Typography component='div' variant='body8'
        sx={{ }}>
        {t('hdSetUpWeb.isDone.heading1')}
      </Typography>
      <Grid container spacing={1} sx={{ mt: theme.spacing(3) }}>
        <Grid item xs={12} md={6}>
          <OptimusButton
            size={'m'}
            buttonText={t('hdSetUpWeb.isDone.yesDone') as string}
            onClick={onYesDoneClick}
            data-testid={TestIds.onboarding.homedepotDoneBtn}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <OptimusButton
            size={'m'}
            customVariant={'secondaryGray'}
            buttonText={t('hdSetUpWeb.isDone.noDone') as string}
            onClick={onNotDoneClick}
            data-testid={TestIds.onboarding.homedepotShowAgainLnk}
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <OnboardingLayout
      customStepper={(
        !userInitiatedHdEnrollment
      ) ?
        <Box sx={{
          maxWidth: theme.spacing(53.75),
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: theme.spacing(3),
          marginBottom: theme.spacing(-8),
          [theme.breakpoints.up('lg')]: {
            marginBottom: theme.spacing(-12),
          },
        }}>
          <OnboardingStepper
            stepLabels={['Step 1', 'Step 2']}
            activeStep={1}
          />
        </Box> : <Box sx={{ mb: theme.spacing(-8) }}></Box>
      }
      customDesktopBanner={null}
    >
      <Box>
        <Box>
          <Snackbar
            open={openErrorSnackbar}
            autoHideDuration={4000}
            onClose={handleErrorClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Alert severity="error" sx={{ width: '100%' }}>
              {t('error.generalMessage')}
            </Alert>
          </Snackbar>
          <Card
            sx={{
              borderRadius: theme.spacing(1.5),
              backgroundColor: theme.palette.common.white,
              mb: theme.spacing(0),
            }}
          >
            <CardContent sx={{ padding: `${theme.spacing(3.125)} !important` }}>
              {!showHDShopQuestion && <><Box sx={{
                mt: theme.spacing(0),
              }}>
                <Typography variant="h2"
                  data-testid={TestIds.onboarding.homedepotShopTitle}
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.grey[800],
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '18px',
                    },
                  }}
                >
                  {t('hdSetUpWeb.heading1b')}
                </Typography>
              </Box>
              <Box sx={{
                mb: theme.spacing(2.5),
              }}>
                <Typography variant="body1"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.grey[700],
                  }}
                >
                  {t('hdSetUpWeb.heading1d')}
                </Typography>
              </Box></>}
              {showHDShopQuestion ?
                <Box sx={{
                  p: theme.spacing(3),
                  borderRadius: theme.spacing(1.5),
                  background: theme.palette.common.white,
                  boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.02), 0 0 8px 0 rgba(0, 0, 0, 0.04)',
                }}>
                  <Box sx={{
                    mt: theme.spacing(0),
                  }}>
                    <Typography variant="h2"
                      data-testid={TestIds.onboarding.homedepotShopTitle}
                      sx={{
                        textAlign: 'center',
                        color: theme.palette.grey[800],
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '18px',
                        },
                      }}
                    >
                      {t('hdSetUpWeb.heading1')}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mb: theme.spacing(2.5),
                  }}>
                    <Typography variant="body1"
                      sx={{
                        textAlign: 'center',
                        color: theme.palette.grey[700],
                      }}
                    >
                      {t('hdSetUpWeb.heading1c')}
                    </Typography>
                  </Box>
                  <Typography sx={{
                    textAlign: 'left',
                    color: theme.palette.brand.primary,
                    mt: theme.spacing(2.5),
                    mb: theme.spacing(2.5),
                  }} >
                    {t('hdSetUpWeb.heading2')}
                  </Typography>
                  <OnboardHDShopQuestionV2 setShowHDShopQuestion={setShowHDShopQuestion} />
                </Box> :
                <form onSubmit={handleSubmit(onSubmit)}>
                  {showExistingAcctQuestion && <Box sx={{
                    p: theme.spacing(3),
                    borderRadius: theme.spacing(1.5),
                    background: theme.palette.common.white,
                    boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.02), 0 0 8px 0 rgba(0, 0, 0, 0.04)',
                  }}>
                    <Typography
                      variant='h15'
                      component={'div'}
                      mb={theme.spacing(3)}
                      textAlign={'left'}
                    >
                      1. {t('hdSetUpWeb.heading4')}
                    </Typography>
                    <Typography
                      variant='body7'
                      component={'div'}
                      mb={theme.spacing(1)}
                      textAlign={'left'}
                    >
                      {t('hdSetUpWeb.heading3')}
                    </Typography>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <Controller
                        name="accountStatus"
                        control={control}
                        defaultValue={selectedOption}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup
                            aria-label="account status"
                            row
                            value={value}
                            onChange={(e) => {
                              onChange(e);
                              handleRadioChange(e); // Call the handler on change
                            }}
                          >
                            <Grid container spacing={1}>
                              {[
                                { value: 'yes', label: 'Yes', id: TestIds.onboarding.homedepotXtraYesBtn },
                                { value: 'no', label: 'No', id: TestIds.onboarding.homedepotXtraNoBtn },
                                { value: 'unsure', label: 'Not sure', id: TestIds.onboarding.homedepotXtraUnsureBtn },
                              ].map((opt) => {
                                const isSelected = value === opt.value;
                                return (<Grid item xs={12} sm={4} key={opt.value}>
                                  <Box
                                    sx={{
                                      border: `1px solid ${isSelected ?
                                        theme.palette.brand.primary :
                                        theme.palette.grey[300]}`,
                                      borderRadius: theme.spacing(1.25),
                                      padding: theme.spacing(1),
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <FormControlLabel
                                      key={opt.id}
                                      id={opt.id}
                                      data-testid={opt.id}
                                      value={opt.value}
                                      control={
                                        <Radio
                                          sx={{
                                            '&.Mui-checked': {
                                              color: theme.palette.brand.primary,
                                            },
                                            '&:hover': {
                                              backgroundColor: theme.palette.brand.surface,
                                            },
                                          }}
                                        />}
                                      label={opt.label}
                                      sx={{ margin: 0, width: '100%' }}
                                      {...register('accountStatus')}
                                    />
                                  </Box>
                                </Grid>);
                              })}
                            </Grid>
                          </RadioGroup>
                        )} />
                    </FormControl>
                  </Box>}
                  {showExistingAcctQuestion &&
                    <Box
                      sx={{
                        p: theme.spacing(3),
                        borderRadius: theme.spacing(1.5),
                        opacity: 0.8,
                        background: theme.palette.grey[300],
                        mt: theme.spacing(3),
                        textAlign: 'left',
                      }}
                    >
                      <Typography
                        variant={'h15'}
                      >
                      2. {t('hdSetUpWeb.heading5')}
                      </Typography>
                    </Box>
                  }
                  {(showSignInOrSignUp || showIsDone || showEmail) &&
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      sx={{
                        cursor: 'pointer',
                        p: theme.spacing(3),
                        borderRadius: theme.spacing(1.5),
                        background: theme.palette.common.white,
                        mt: theme.spacing(3),
                        textAlign: 'left',
                        boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.02), 0 0 8px 0 rgba(0, 0, 0, 0.04)',
                      }}
                      onClick={handleReturnToShowExistAccQns}
                    >
                      <Typography
                        variant={'h15'}
                      >
                      1. {t('hdSetUpWeb.heading4')}
                      </Typography>
                      <ChevronRightIcon/>
                    </Box>
                  }
                  {showSignInOrSignUp && <SignInOrSignUpCard />}
                  {showIsDone && <IsDoneCard />}
                  {(showExistingAcctQuestion || showIsDone) &&
                    <Box
                      sx={{
                        p: theme.spacing(3),
                        borderRadius: theme.spacing(1.5),
                        opacity: 0.8,
                        background: theme.palette.grey[300],
                        mt: theme.spacing(3),
                        textAlign: 'left',
                      }}
                    >
                      <Typography
                        variant={'h15'}
                      >
                      3. {t('hdSetUpWeb.heading6')}
                      </Typography>
                    </Box>
                  }
                  {(showEmail) &&
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      sx={{
                        cursor: 'pointer',
                        p: theme.spacing(3),
                        borderRadius: theme.spacing(1.5),
                        background: theme.palette.common.white,
                        mt: theme.spacing(3),
                        textAlign: 'left',
                        boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.02), 0 0 8px 0 rgba(0, 0, 0, 0.04)',
                      }}
                      onClick={handleReturnToShowLinkAccount}
                    >
                      <Typography
                        variant={'h15'}
                      >
                      2. {t('hdSetUpWeb.heading5')}
                      </Typography>
                      <ChevronRightIcon/>
                    </Box>
                  }
                  {showEmail &&
                    <Box sx={{
                      textAlign: 'left',
                      mt: theme.spacing(3),
                      p: theme.spacing(3),
                      borderRadius: theme.spacing(1.5),
                      background: theme.palette.common.white,
                      boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.02), 0 0 8px 0 rgba(0, 0, 0, 0.04)',
                    }}>
                      <Typography
                        variant={'h15'}
                        component={'div'}
                      >
                      3. {t('hdSetUpWeb.heading6')}
                      </Typography>
                      <Typography variant={'body7'} component={'div'}
                        sx={{ mb: theme.spacing(1), mt: theme.spacing(3) }}>
                        {`${t('hdSetUpWeb.enterEmail.heading1')}*`}
                      </Typography>
                      <Box>
                        <Controller
                          name="email"
                          control={control}
                          defaultValue={hdEmail}
                          render={({
                            field: {
                              onChange, onBlur, value,
                            },
                          }) => <TextField
                            value={value} // Set the value directly
                            onChange={(event) => {
                              onChange(event);
                              setHdEmail(event.target.value);
                            }}
                            onBlur={onBlur}
                            data-testid={TestIds.onboarding.homedepotEmailAdressTxt}
                            placeholder={'Enter your email address'}
                            InputProps={{
                              sx: {
                                borderRadius: theme.spacing(1.5),
                                border: `1px solid  ${theme.palette.grey[200]}`,
                                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
                                background: theme.palette.common.white,
                                fontWeight: '400',
                                fontSize: theme.spacing(2.25),
                                paddingLeft: theme.spacing(2),
                              },
                            }}
                            sx={{
                              width: '100%',
                              '& .MuiOutlinedInput-root.Mui-focused': {
                                '& > fieldset': {
                                  borderColor:
                                    formState?.errors?.email &&
                                    formState.errors.email.type !== 'required' ?
                                      red[500] :
                                      theme.palette.brand.primary,
                                },
                              },
                            }}
                          />}
                          rules={{
                            required: 'Email is required',
                            pattern: {
                              value: emailRegex,
                              message: `${t('error.invalidEmail2')}`,
                            },
                          }}
                        />
                        {(formState?.errors?.email && formState.errors.email.type !== 'required') &&
                          <Alert
                            icon={false}
                            sx={{
                              background: theme.palette.common.white,
                              color: theme.palette.error.main,
                              p: 0,
                              fontSize: '14px',
                              fontWeight: 500,
                              '& .MuiAlert-message': {
                                pb: 0,
                              },
                            }}
                            severity='error'>
                            {formState?.errors.email.message}
                          </Alert>}
                      </Box>
                      <OptimusButton
                        type={'submit'}
                        size={'m'}
                        buttonText={
                          loading ?
                            <CircularProgress color="inherit" size={24} /> :
                            <Typography component='span' data-testid={TestIds.onboarding.homedepotCompleteEnrollmentBtn} sx={{ fontWeight: 600 }}>
                              {t('button.completeHdWebSetupB')}
                            </Typography>
                        }
                        disabled={!hdEmail || !formState.isValid || loading}
                        sx={{
                          mt: theme.spacing(3),
                        }}
                      />
                    </Box>}
                </form>}
            </CardContent>
          </Card>
          {!showHDShopQuestion &&
          <Box sx={{ mt: theme.spacing(-4.5), mb: theme.spacing(1) }}>
            <PostponeHdSetup
              optionalTextStyles={{
                color: theme.palette.brand.primary,
              }}
            />
          </Box>
          }
        </Box>
      </Box>
    </OnboardingLayout>
  );
}
